import { Box, useColorMode } from '@chakra-ui/react';
import { GraphQLReqStatus } from '@gamma/ui/progress';
import moment from 'moment';
import { useEffect, useMemo, useState } from 'react';
import {
  EntityFilter,
  useGetChartTimelineFilters,
  useGetDetectionTimelineSummary,
} from '../../../../queries';
import {
  Detection,
  DetectionAlertSummary,
} from '@investigator/shared/generated/graphql';

import { OptionType } from '@gamma/ui/form-fields';
import { safeParse } from 'secure-json-parse';
import { i18n } from '../../../../../../shared/localization';
import { RelatedDetectionsAlerts } from './Components/RelatedDetectionsAlerts';
import { ApexDetectionsChart } from './Components/RelatedDetectionsChart';
const { correlationChart } = i18n.pages.detections;
const { headings } = correlationChart;

interface RelatedDetectionsProps {
  detection: Detection;
  suricataRuleText: string;
  selectedDetection?: Detection;
  selectedDetectionId?: string;
  setIsSlideDrawerOpen?: React.Dispatch<
    React.SetStateAction<boolean | undefined>
  >;
  setSelectedDetectionId: React.Dispatch<
    React.SetStateAction<string | undefined>
  >;
  getDetectionsLoading: boolean;
}

export const RelatedDetections = ({
  detection,
  suricataRuleText,
  selectedDetection,
  selectedDetectionId,
  getDetectionsLoading,
  setSelectedDetectionId,
}: RelatedDetectionsProps) => {
  const { colorMode } = useColorMode();
  const [ts, setTs] = useState(0);

  useEffect(() => {
    setTs(moment().unix());
  }, [colorMode, selectedDetectionId]);

  const { tenant, detection_id, latest_start_timestamp } = detection;

  const latestAlertDateTime = moment((latest_start_timestamp ?? 0) * 1000);
  const currentDiffLatest = moment
    .duration(moment().diff(latestAlertDateTime))
    .get('days');
  const showPastFrameAlerts =
    moment.duration(moment().diff(latestAlertDateTime)).get('months') > 0 ||
    moment.duration(moment().diff(latestAlertDateTime)).get('years') > 0;

  const endDateTimeOne =
    showPastFrameAlerts || currentDiffLatest >= 15
      ? moment((latest_start_timestamp ?? 0) * 1000).add(15, 'days')
      : moment();

  const endDateTimeTwo =
    showPastFrameAlerts || currentDiffLatest >= 15
      ? moment((latest_start_timestamp ?? 0) * 1000).add(15, 'days')
      : moment();

  const end = endDateTimeOne;
  const start = endDateTimeTwo.subtract(30, 'days');

  const [startSet, setStartSet] = useState<number | undefined>();
  const [endSet, setEndSet] = useState<number | undefined>();

  useEffect(() => {
    if (end && !endSet) {
      setEndSet(end.unix());
    }
    if (start && !startSet) {
      setStartSet(start.unix());
    }
  }, [end, start, endSet, startSet]);

  const maxRows = 5;

  const [interval, setInterval] = useState<number>(86400);

  const [alertFilters, setAlertFilters] = useState<OptionType[]>([]);
  const [severityFilters, setSeverityFilters] = useState<OptionType[]>([]);
  const [sourceFilters, setSourceFilters] = useState<OptionType[]>([]);
  const [destFilters, setDestFilters] = useState<OptionType[]>([]);
  const [sourceDestFilters, setSourceDestFilters] = useState<OptionType[]>([]);

  const skipGetChartTimelineFilters =
    !tenant || !detection_id || !endSet || !startSet || !interval;

  const {
    loading: getChartTimelineFiltersLoading,
    error: getChartTimelineFiltersError,
    data: getChartTimelineFiltersData,
    fetchMore: getChartTimelineFiltersFetchMore,
  } = useGetChartTimelineFilters({
    skip: skipGetChartTimelineFilters,
    fetchPolicy: 'cache-and-network',
    variables: {
      end: endSet,
      start: startSet,
      interval,
      detection_id,
      size: 10,
      detection_tenant: tenant,
      source_offset: 0,
      dest_offset: 0,
      source_dest_pair_offset: 0,

      severity_filter:
        severityFilters.length > 0
          ? severityFilters.map((sev) => sev.value)
          : undefined,

      entity_filter: (() => {
        const sourceArr = sourceFilters.map((f) => f.value as string);
        const destArr = destFilters.map((f) => f.value as string);
        const parsedPairs: { source: string[]; dest: string[] } = {
          source: [],
          dest: [],
        };
        sourceDestFilters.forEach((f) => {
          const parsedVal = safeParse(f.value as string);
          parsedPairs.source.push(parsedVal.source);
          parsedPairs.dest.push(parsedVal.dest);
        });

        const entityFilterInput: Partial<EntityFilter> = {};

        if (sourceArr.length > 0) {
          entityFilterInput.source = { source_entities: sourceArr };
        }
        if (destArr.length > 0) {
          entityFilterInput.dest = { dest_entities: destArr };
        }
        if (parsedPairs.source.length > 0 && parsedPairs.dest.length > 0) {
          entityFilterInput.source_dest = {
            source_entities: parsedPairs.source,
            dest_entities: parsedPairs.dest,
          };
        }
        return Object.keys(entityFilterInput).length > 0
          ? entityFilterInput
          : undefined;
      })(),

      alert_name_filter:
        alertFilters.length > 0
          ? alertFilters.map((alert) => safeParse(alert.value as string))
          : undefined,
    },
  });

  const { alert_name_filter, entity_filter, severity_filter } =
    getChartTimelineFiltersData?.getChartTimelineFilters || {};

  const totalDestItems = entity_filter?.dest?.total_items ?? 0;

  const alertOptions = useMemo(() => {
    if (!alert_name_filter?.length) return [];
    return alert_name_filter.map((alert) => ({
      value: JSON.stringify(alert),
      label: alert.alert_name,
    }));
  }, [alert_name_filter]);

  const severityOptions = useMemo(() => {
    if (!severity_filter?.length) return [];
    return severity_filter
      .map((sev) => {
        switch (sev) {
          case 'low':
            return { value: 'low', label: headings.low };
          case 'medium':
            return { value: 'medium', label: headings.medium };
          case 'high':
            return { value: 'high', label: headings.high };
          default:
            return null;
        }
      })
      .filter(Boolean) as OptionType[];
  }, [severity_filter]);

  const [sourceOptions, destOptions, sourceDestOptions] = useMemo(() => {
    if (!entity_filter) return [[], [], []];

    const { source, dest, source_dest } = entity_filter;

    const sourceEntities = source?.source_entities || [];
    const sOptions: OptionType[] = sourceEntities.map((src) => ({
      value: src,
      label: src,
      type: 'source',
    }));

    const destEntities = dest?.dest_entities || [];
    const dOptions: OptionType[] = destEntities.map((dst) => ({
      value: dst,
      label: dst,
      type: 'dest',
    }));

    const { source_entities = [], dest_entities = [] } = source_dest || {};
    const sdOptions: OptionType[] = source_entities.flatMap((src) => {
      return dest_entities.map((dst) => {
        return {
          value: JSON.stringify({ source: src, dest: dst }),
          label: `${src} - ${dst}`,
          type: 'source_dest',
        } as OptionType;
      });
    });

    return [sOptions, dOptions, sdOptions];
  }, [entity_filter]);

  const variables = useMemo(() => {
    return {
      end: endSet,
      start: startSet,
      interval,
      detection_id,
      size: 10,
      detection_tenant: tenant,
      entity_filter: (() => {
        const sourceArr = sourceFilters.map((f) => f.value as string);
        const destArr = destFilters.map((f) => f.value as string);
        const parsedPairs: { source: string[]; dest: string[] } = {
          source: [],
          dest: [],
        };

        sourceDestFilters.forEach((f) => {
          const parsedVal = safeParse(f.value as string);
          parsedPairs.source.push(parsedVal.source);
          parsedPairs.dest.push(parsedVal.dest);
        });

        const entityFilterInput: Partial<EntityFilter> = {};

        if (sourceArr.length > 0) {
          entityFilterInput.source = { source_entities: sourceArr };
        }
        if (destArr.length > 0) {
          entityFilterInput.dest = { dest_entities: destArr };
        }
        if (parsedPairs.source.length > 0 && parsedPairs.dest.length > 0) {
          entityFilterInput.source_dest = {
            source_entities: parsedPairs.source,
            dest_entities: parsedPairs.dest,
          };
        }
        return Object.keys(entityFilterInput).length > 0
          ? entityFilterInput
          : undefined;
      })(),

      severity_filter:
        severity_filter && severity_filter.length > 0
          ? severity_filter
          : undefined,
      alert_name_filter:
        alert_name_filter && alert_name_filter.length > 0
          ? alert_name_filter
          : undefined,
    };
  }, [
    endSet,
    startSet,
    interval,
    detection_id,
    tenant,
    sourceFilters,
    destFilters,
    sourceDestFilters,
    severity_filter,
    alert_name_filter,
  ]);

  const { loading, error, data } = useGetDetectionTimelineSummary({
    skip: skipGetChartTimelineFilters || getChartTimelineFiltersLoading,
    fetchPolicy: 'cache-and-network',
    variables,
  });

  const {
    getCurrentDetectionTimelineSummary,
    getDetectionSourceDestinationTimelineSummary,
    getDetectionSourceTimelineSummary,
    getDetectionDestinationTimelineSummary,
  } = data || {};

  const currentDetectionDetections = useMemo(
    () =>
      (
        getCurrentDetectionTimelineSummary?.summary?.flatMap(
          (summary) => summary?.detections ?? [],
        ) || []
      ).filter((d): d is DetectionAlertSummary => d != null),
    [getCurrentDetectionTimelineSummary],
  );

  const detectionSourceDestinationDetections = useMemo(
    () =>
      (
        getDetectionSourceDestinationTimelineSummary?.summary?.flatMap(
          (summary) => (summary ? summary.detections : []),
        ) || []
      ).filter((d): d is DetectionAlertSummary => d != null),
    [getDetectionSourceDestinationTimelineSummary],
  );

  const detectionSourceDetections = useMemo(
    () =>
      (
        getDetectionSourceTimelineSummary?.summary?.flatMap((summary) =>
          summary ? summary.detections : [],
        ) || []
      ).filter((d): d is DetectionAlertSummary => d != null),
    [getDetectionSourceTimelineSummary],
  );

  const detectionDestinationDetections = useMemo(
    () =>
      (
        getDetectionDestinationTimelineSummary?.summary?.flatMap((summary) =>
          summary ? summary.detections : [],
        ) || []
      ).filter((d): d is DetectionAlertSummary => d != null),
    [getDetectionDestinationTimelineSummary],
  );

  const [chartDetections, setChartDetections] = useState<{
    currentDetectionDetections?: DetectionAlertSummary[];
    detectionSourceDetections?: DetectionAlertSummary[];
    detectionDestinationDetections?: DetectionAlertSummary[];
    detectionSourceDestinationDetections?: DetectionAlertSummary[];
  }>({
    currentDetectionDetections: [],
    detectionSourceDetections: [],
    detectionDestinationDetections: [],
    detectionSourceDestinationDetections: [],
  });

  useEffect(() => {
    setChartDetections({
      currentDetectionDetections,
      detectionSourceDetections,
      detectionDestinationDetections,
      detectionSourceDestinationDetections,
    });
  }, [
    currentDetectionDetections,
    detectionSourceDetections,
    detectionDestinationDetections,
    detectionSourceDestinationDetections,
  ]);

  if (
    loading ||
    getChartTimelineFiltersLoading ||
    error ||
    getChartTimelineFiltersError
  ) {
    return (
      <GraphQLReqStatus
        error={error || getChartTimelineFiltersError}
        loading={!!loading || getChartTimelineFiltersLoading}
      />
    );
  }

  return (
    <Box key={ts}>
      <ApexDetectionsChart
        end={end.unix()}
        start={start.unix()}
        maxRows={maxRows}
        chartsData={data}
        interval={interval}
        setInterval={setInterval}
        alertOptions={alertOptions}
        alertFilters={alertFilters}
        setAlertFilters={setAlertFilters}
        sourceOptions={sourceOptions}
        sourceFilters={sourceFilters}
        setSourceFilters={setSourceFilters}
        destOptions={destOptions}
        destFilters={destFilters}
        setDestFilters={setDestFilters}
        totalDestItems={totalDestItems}
        sourceDestOptions={sourceDestOptions}
        sourceDestFilters={sourceDestFilters}
        setSourceDestFilters={setSourceDestFilters}
        severityOptions={severityOptions}
        severityFilters={severityFilters}
        setSeverityFilters={setSeverityFilters}
        setSelectedDetectionId={setSelectedDetectionId}
        latest_start_timestamp={latest_start_timestamp ?? 0}
        fetchMoreTimelineFilters={getChartTimelineFiltersFetchMore}
      />
      <RelatedDetectionsAlerts
        loading={loading}
        error={error}
        data={data}
        detection={detection}
        chartDetections={chartDetections}
        suricataRuleText={suricataRuleText}
        selectedDetection={selectedDetection}
        getDetectionsLoading={getDetectionsLoading}
        setSelectedDetectionId={setSelectedDetectionId}
      />
    </Box>
  );
};
